/**
 * FormBuilder field config for meeting room selection.
 * Generic — name and label must be provided by consumer.
 *
 * Supports two rendering modes:
 *
 * 1. Flat options — pass `roomOptions` for a plain search-and-pick list.
 * 2. Grouped options (Figma recipe in
 *    `components/packages/core/.../SelectVariations.stories.tsx`
 *    `CustomRenderOptionWithGroups[Multi]`) — pass `groupedOptions` with
 *    accordion-style groups. Each option's `data` typically carries
 *    `{ status: 'available'|'busy', capacity: number }`. Consumers can
 *    override the per-option / trigger / chip render functions.
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import type { SelectOption } from '@kula-ai/components';

export type MeetingRoomOptionStatus = 'available' | 'busy';

export interface MeetingRoomOptionData {
  status?: MeetingRoomOptionStatus;
  capacity?: number;
  [key: string]: unknown;
}

export interface MeetingRoomGroup {
  label: string;
  options: SelectOption[];
}

interface RenderOptionCtx {
  isSelected: boolean;
}

interface RemoveCtx {
  onRemove: () => void;
}

interface AccordionSectionArg {
  label: string;
  options: SelectOption[];
}

interface AccordionHeaderState {
  isExpanded: boolean;
  onToggle: () => void;
}

interface UseMeetingRoomFieldConfigProps {
  name: string;
  label: string;
  condition?: Record<string, unknown>;
  placeholder?: string;
  isMulti?: boolean;
  /** Flat option list — ignored when `groupedOptions` is supplied. */
  roomOptions?: SelectOption[];
  /** Accordion-style groups (Figma "Meeting room" recipe). */
  groupedOptions?: MeetingRoomGroup[];
  /** Shows a spinner in the dropdown while availability polls. */
  loading?: boolean;
  /** Message to show inside the menu during loading (string or node). */
  loadingMessage?: string;
  /** Custom per-option row. Overrides the default status + capacity layout. */
  renderOption?: (_option: SelectOption, _ctx: RenderOptionCtx) => ReactNode;
  /** Trigger value renderer (single mode); for grouped we default to a status dot. */
  renderTriggerValue?: (
    _selected: SelectOption | SelectOption[] | null | undefined
  ) => ReactNode;
  /** Chip renderer for multi-select. */
  renderTag?: (_option: SelectOption, _ctx: RemoveCtx) => ReactNode;
  /** Full replacement for the accordion group header — office name,
   *  count, chevron, etc. */
  renderGroupHeader?: (
    _section: AccordionSectionArg,
    _state: AccordionHeaderState
  ) => ReactNode;
  /** Draws 1px dividers between consecutive option rows inside an
   *  expanded accordion group. */
  showOptionDividers?: boolean;
}

export function useMeetingRoomFieldConfig(
  props: UseMeetingRoomFieldConfigProps
) {
  const flatOptions = useMemo(
    () => props.roomOptions ?? [],
    [props.roomOptions]
  );

  const filterFlatOptions = useCallback(
    (query: string) => {
      if (!query) return flatOptions;
      const q = query.toLowerCase();
      return flatOptions.filter((o) => o.label.toLowerCase().includes(q));
    },
    [flatOptions]
  );

  const fieldConfig = useMemo(() => {
    const common = {
      type: 'select' as const,
      name: props.name,
      label: props.label,
      condition: props.condition,
    };

    const loadingProps = {
      loading: Boolean(props.loading),
      loadingMessage: props.loadingMessage ?? 'Loading meeting rooms…',
    };

    if (props.groupedOptions && props.groupedOptions.length > 0) {
      return {
        ...common,
        options: props.groupedOptions,
        fieldProps: {
          searchable: true,
          mode: (props.isMulti ? 'multiple' : 'single') as
            | 'single'
            | 'multiple',
          placeholder: props.placeholder ?? 'Select meeting room',
          groupDisplay: 'accordion' as const,
          optionIndicator: 'checkbox' as const,
          showOptionDividers: props.showOptionDividers ?? true,
          ...loadingProps,
          ...(props.renderOption ? { renderOption: props.renderOption } : {}),
          ...(props.renderGroupHeader
            ? { renderGroupHeader: props.renderGroupHeader }
            : {}),
          ...(props.renderTriggerValue
            ? { renderTriggerValue: props.renderTriggerValue }
            : {}),
          ...(props.renderTag ? { renderTag: props.renderTag } : {}),
        },
      };
    }

    return {
      ...common,
      options: flatOptions,
      fieldProps: {
        searchable: true,
        mode: (props.isMulti ? 'multiple' : 'single') as 'single' | 'multiple',
        placeholder: props.placeholder ?? 'Select meeting room',
        filterOptions: filterFlatOptions,
        ...loadingProps,
      },
    };
  }, [
    props.name,
    props.label,
    props.condition,
    props.isMulti,
    props.placeholder,
    props.groupedOptions,
    props.loading,
    props.loadingMessage,
    props.renderOption,
    props.renderGroupHeader,
    props.renderTriggerValue,
    props.renderTag,
    props.showOptionDividers,
    flatOptions,
    filterFlatOptions,
  ]);

  return { fieldConfig, options: flatOptions };
}
